import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import {
  useGetCourseBySlugQuery,
  useGetCourseProgressQuery,
} from "@/features/api/courseApi";

import { useCheckEnrollmentQuery, useEnrollCourseMutation  } from "@/features/api/orderApi";

import {
  Loader2,
  Clock,
  Lock,
  Languages,
  ListVideo,
  GraduationCap,
  ChevronRight,
  Video,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  const [isRedirecting, setIsRedirecting] = useState(false);

  /* ================= FETCH COURSE ================= */
  const { data, isLoading, error } = useGetCourseBySlugQuery(slug);
  const course = data?.course;

  /* ================= MEDIA NORMALIZER ================= */
  const media = (url) =>
    url?.startsWith("/uploads") ? `https://fetc.in${url}` : url || "";

  /* ================= ENROLLMENT ================= */
  const { data: enrollmentData } = useCheckEnrollmentQuery(
    course?._id,
    { skip: !course?._id || !user }
  );

  const isEnrolled = enrollmentData?.enrolled ?? false;

  const { data: progressData } = useGetCourseProgressQuery(
    course?._id,
    { skip: !isEnrolled }
  );

  const [enrollCourse, { isLoading: enrolling }] =
    useEnrollCourseMutation ();

  useEffect(() => {
    if (error) toast.error("Failed to load course");
  }, [error]);

  const buttonText = useMemo(() => {
  if (!course) return "";

  if (isRedirecting) return "Redirecting...";

  if (isEnrolled) {
    return progressData?.percentage > 0
      ? "Continue Learning"
      : "Start Course";
  }

  return course.price > 0
    ? `Buy Now ₹${course.price}`
    : "Enroll for Free";
}, [isEnrolled, progressData?.percentage, course, isRedirecting]);


  if (isLoading) {
  return (
    <div className="container py-12">
      <Skeleton className="h-72 w-full rounded-xl" />
    </div>
  );
}

if (!course) {
  return (
    <div className="container py-12 text-center">
      <p className="text-gray-500">Course not found.</p>
    </div>
  );
}
 
const handleCTA = () => {
  if (!user) {
    navigate("/login");
    return;
  }

  if (isRedirecting) return;

  if (isEnrolled) {
    setIsRedirecting(true);
    navigate(`/courses/${course.slug}/learn`);
    return;
  }

  setIsRedirecting(true);

navigate(`/checkout?slug=${course.slug}`);

};


  const instructorInit =
    course.instructorName
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "IN";

  const cleanHtml = parse(DOMPurify.sanitize(course.content || ""));

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>{course.title}</title>
      </Helmet>

         <div className="container px-4 py-8 mx-auto">
              <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbSeparator />

                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/courses">Courses</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbSeparator />

                <BreadcrumbItem>
                  <BreadcrumbPage>{course.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

        <div className="grid lg:grid-cols-3 gap-10">

          {/* ================= LEFT ================= */}
          <div className="lg:col-span-2 space-y-8">

            <h1 className="text-4xl font-bold">{course.title}</h1>
            <p className="text-gray-600">{course.metaDescription}</p>

            {/* Instructor */}
            <div className="flex items-center gap-4">
              <Avatar>
                {course.instructorImage ? (
                  <AvatarImage src={media(course.instructorImage)} />
                ) : (
                  <AvatarFallback>{instructorInit}</AvatarFallback>
                )}
              </Avatar>

              <div>
                <div className="font-medium">
                  {course.instructorName}
                </div>
                <div className="text-sm text-gray-500">
                  {course.instructorBio}
                </div>
              </div>
            </div>

            {/* Media */}
            <div className="rounded-xl overflow-hidden shadow">
              {course.videoUrl ? (
                <video
                  controls
                  src={media(course.videoUrl)}
                  className="w-full h-[420px] object-cover bg-black"
                />
              ) : (
                <img
                  src={media(course.featuredImage)}
                  alt={course.title}
                  className="w-full h-[420px] object-cover"
                />
              )}
            </div>

            {/* ================= LESSONS ================= */}
         <Card>
  <CardHeader>
    <CardTitle>Course Lessons</CardTitle>
  </CardHeader>

  <CardContent className="space-y-3">
    {course.lessons?.length ? (
      course.lessons.map((lesson, index) => {
        const firstVideo =
          lesson.videoUrls?.[0] || lesson.videoUrl;

        const isPreview = lesson.isPreview || false;
        const isLocked = !isEnrolled && !isPreview;

        return (
          <div
            key={lesson._id}
            className={`flex justify-between items-center p-3 border rounded-lg transition
              ${isLocked ? "bg-gray-100 opacity-70" : "hover:bg-gray-50"}
            `}
          >
            {/* LEFT */}
            <div className="flex items-center gap-3">
              <div
                className={`h-8 w-8 flex items-center justify-center rounded-full font-semibold
                ${
                  isLocked
                    ? "bg-gray-300 text-gray-600"
                    : "bg-primary/10 text-primary"
                }`}
              >
                {index + 1}
              </div>

              <div>
                <p
                  className={`font-medium ${
                    isLocked ? "text-gray-500" : ""
                  }`}
                >
                  {lesson.title}
                </p>

                {lesson.duration && (
                  <p className="text-xs text-gray-500">
                    {lesson.duration}
                  </p>
                )}
              </div>
            </div>

            {/* RIGHT */}
            {isLocked ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Lock className="w-4 h-4" />
                Locked
              </div>
            ) : firstVideo ? (
              <a
                href={media(firstVideo)}
                target="_blank"
                rel="noreferrer"
                className="text-primary text-sm flex items-center gap-1"
              >
                <Video className="w-4 h-4" />
                {isPreview ? "Preview" : "Watch"}
              </a>
            ) : (
              <ChevronRight className="text-gray-400" />
            )}
          </div>
        );
      })
    ) : (
      <p>No lessons available.</p>
    )}
  </CardContent>
</Card>

            {/* ================= ABOUT ================= */}
            <Card>
              <CardHeader>
                <CardTitle>About This Course</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  {cleanHtml}
                </div>
              </CardContent>
            </Card>

           

          </div>

          {/* ================= SIDEBAR ================= */}
          <aside className="sticky top-8">
            <Card>
              <CardHeader>
                <div className="text-4xl font-bold">
                  {course.price > 0 ? `₹${course.price}` : "Free"}
                </div>
              </CardHeader>

              <CardContent>

                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleCTA}
                  disabled={enrolling || isRedirecting}
                >
                  {(enrolling || isRedirecting) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {buttonText}
                </Button>

                {isEnrolled && (
                  <div className="mt-4">
                    <Progress
                      value={progressData?.percentage || 0}
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      {progressData?.percentage || 0}% completed
                    </p>
                  </div>
                )}

                <Separator className="my-4" />

                <div className="space-y-3 text-sm">

                  <div className="flex justify-between">
                    <span>Duration</span>
                    <span>{course.duration || "N/A"}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Lessons</span>
                    <span>{course.lessons?.length || 0}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Certificate</span>
                    <span>{course.certificate ? "Yes" : "No"}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Language</span>
                    <span>{course.language}</span>
                  </div>

                </div>

              </CardContent>
            </Card>
          </aside>

        </div>
      </div>
    </div>
  );
}